import { useHotkeys } from 'react-hotkeys-hook'
import { useNotes } from './useNotes'
import { useAutoSave } from './useAutoSave'

interface UseKeyboardShortcutsOptions {
  noteId?: string | null | undefined
  content?: string
  title?: string
  onFocusEditor?: () => void
  onFocusSearch?: () => void
}

export const useKeyboardShortcuts = ({
  noteId,
  content = '',
  title,
  onFocusEditor,
  onFocusSearch
}: UseKeyboardShortcutsOptions = {}) => {
  const { createNote, deleteNote, selectedNoteId } = useNotes()
  const { saveImmediately } = useAutoSave({ noteId: noteId || null, content, title, enabled: !!noteId })

  // Create new note (Cmd + N on Mac, Ctrl + N on PC)
  useHotkeys('mod+n', async (e) => {
    e.preventDefault()
    const note = await createNote()
    if (note && onFocusEditor) {
      // Small delay to ensure the note is selected
      setTimeout(onFocusEditor, 100)
    }
  }, {
    enableOnFormTags: true,
    enableOnContentEditable: true
  })

  // Save note (Cmd + S on Mac, Ctrl + S on PC)
  useHotkeys('mod+s', (e) => {
    e.preventDefault()
    if (noteId) {
      saveImmediately()
    }
  }, {
    enableOnFormTags: true,
    enableOnContentEditable: true
  })

  // Delete note (Cmd + Backspace on Mac, Ctrl + Backspace on PC)
  useHotkeys('mod+backspace', async (e) => {
    e.preventDefault()
    if (selectedNoteId && confirm('Are you sure you want to delete this note?')) {
      await deleteNote(selectedNoteId)
    }
  }, {
    enableOnFormTags: true,
    enableOnContentEditable: true
  })

  // Focus search (Cmd + F on Mac, Ctrl + F on PC)
  useHotkeys('mod+f', (e) => {
    e.preventDefault()
    if (onFocusSearch) {
      onFocusSearch()
    }
  }, {
    enableOnFormTags: true,
    enableOnContentEditable: true
  })

  // Focus editor (Cmd + E on Mac, Ctrl + E on PC)
  useHotkeys('mod+e', (e) => {
    e.preventDefault()
    if (onFocusEditor) {
      onFocusEditor()
    }
  }, {
    enableOnFormTags: true,
    enableOnContentEditable: true
  })

  return {
    // Expose manual actions for components that need them
    saveImmediately
  }
}